import { Link } from "react-router-dom";
import { formatDate } from "../format.js";
import { getStaffStudentGoals, type StudentGoal } from "../studentGoals.js";
import { useAsync } from "../useAsync.js";

type LessonGoalSummary = Pick<
  StudentGoal,
  | "id"
  | "title"
  | "description"
  | "status"
  | "targetDate"
  | "repertoireTitle"
  | "repertoireComposer"
  | "linkedPracticeTasks"
>;

type CurrentGoalsSummary = {
  goals: LessonGoalSummary[];
  total: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isLessonGoalSummary(value: unknown): value is LessonGoalSummary {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.title === "string" &&
    (typeof value.description === "string" || value.description === null) &&
    (value.status === "active" || value.status === "paused") &&
    (typeof value.targetDate === "string" || value.targetDate === null) &&
    (typeof value.repertoireTitle === "string" || value.repertoireTitle === null) &&
    (typeof value.repertoireComposer === "string" || value.repertoireComposer === null) &&
    typeof value.linkedPracticeTasks === "number" &&
    Number.isSafeInteger(value.linkedPracticeTasks) &&
    value.linkedPracticeTasks >= 0
  );
}

async function loadCurrentGoals(studentId: string): Promise<CurrentGoalsSummary> {
  const response: unknown = await getStaffStudentGoals(studentId);
  const current = isRecord(response) ? response.current : null;
  if (!Array.isArray(current) || !current.every(isLessonGoalSummary)) {
    throw new Error("Current goals returned an unexpected response.");
  }
  const goals = current.filter(isLessonGoalSummary);
  return { goals: goals.slice(0, 3), total: goals.length };
}

export function LessonStudentGoalsPanel({
  studentId,
  progressHref,
}: {
  studentId: string;
  progressHref: string;
}) {
  const { data, error, loading, reload } = useAsync(
    () => loadCurrentGoals(studentId),
    [studentId],
  );

  return (
    <section className="panel lesson-student-goals" aria-labelledby="lesson-student-goals-heading">
      <div className="lesson-student-goals-header">
        <div>
          <h2 id="lesson-student-goals-heading">Current learning goals</h2>
          <p className="small muted">A quick view of the direction already agreed with this student.</p>
        </div>
        <Link className="quiet small" to={progressHref}>Goals &amp; progress</Link>
      </div>

      {loading && !data && <p className="small muted" role="status">Loading current goals…</p>}
      {error && (
        <div className="small" role="alert">
          <p>Current goals could not be loaded. The lesson note is still available.</p>
          <button type="button" className="quiet small" onClick={reload}>Retry goals</button>
        </div>
      )}
      {data && data.goals.length === 0 && !error && (
        <p className="small muted">No current goals are recorded yet.</p>
      )}
      {data && data.goals.length > 0 && (
        <>
          <ul className="lesson-student-goals-list">
            {data.goals.map((goal) => (
              <li className="lesson-student-goal" key={goal.id}>
                <div className="lesson-student-goal-title">
                  <strong>{goal.title}</strong>
                  <span className={goal.status === "paused" ? "tag warn" : "tag"}>
                    {goal.status === "paused" ? "Paused" : "Active"}
                  </span>
                </div>
                {goal.repertoireTitle && (
                  <p className="small muted">
                    {goal.repertoireComposer ? goal.repertoireComposer + " — " : ""}
                    {goal.repertoireTitle}
                  </p>
                )}
                {goal.description && <p className="small">{goal.description}</p>}
                {/* Noon UTC keeps a date-only goal on the same London calendar day in GMT and BST. */}
                <p className="small muted lesson-student-goal-meta">
                  {goal.targetDate
                    ? "Target " + formatDate(goal.targetDate + "T12:00:00.000Z")
                    : "No target date"}
                  {goal.linkedPracticeTasks > 0 &&
                    " · " + goal.linkedPracticeTasks + " linked " +
                      (goal.linkedPracticeTasks === 1 ? "practice task" : "practice tasks")}
                </p>
              </li>
            ))}
          </ul>
          {data.total > data.goals.length && (
            <p className="small muted">Showing {data.goals.length} of {data.total} current goals.</p>
          )}
        </>
      )}
    </section>
  );
}
